import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { apiGet, apiPost } from 'src/api/client';
// import { LOCAL_ACCESS_TOKEN } from 'src/utils/jwt';
import type { RootState } from '../../store';

export type RequestStatus = 'idle' | 'loading' | 'success' | 'error';

export enum AuthErrorType {
  ERR_USER_NOT_FOUND = 'ERR_USER_NOT_FOUND',
  ERR_USER_EMAIL_NOT_FOUND = 'ERR_USER_EMAIL_NOT_FOUND',
  ERR_USER_DISABLED = 'ERR_USER_DISABLED',
  ERR_MDP_EXPIRED = 'ERR_MDP_EXPIRED',
  ERR_LOGIN_ATTEMPTS_EXCEEDED = 'LOGIN_ATTEMPTS_EXCEEDED',
  SUCCESS_CODE = 'SUCCESS_CODE',
  ERR_TECHNICAL = 'ERR_TECHNICAL',
  ERR_USER_INACTIF = 'ERR_USER_INACTIF',
  ERR_EMAIL_NOT_SEND = 'ERR_EMAIL_NOT_SEND',
  VERIFY_OTP_INVALID = 'VERIFY_OTP_INVALID',
	VERIFY_OTP_EXPIRED = 'VERIFY_OTP_EXPIRED',
  VERIFY_OTP_ATTEMPTS_EXCEEDED = 'VERIFY_OTP_ATTEMPTS_EXCEEDED'
}

// Define a type for the slice state
export interface AuthPayload {
  email: string;
  password: string;
}

export interface TwoFactorAuthPayload {
  uuid: string;
  otp: string;
}

interface User {
  profile: string;
  login: string;
  nom: string;
  prenom: string;
}

interface AuthState {
  isInitialized: boolean;
  isAuthenticated: boolean;
  is2FA: boolean;
  uuid: string;
  status: RequestStatus;
  error: AuthErrorType;
  user?: User;
}

// Define the initial state using that type
const initialState: AuthState = {
  isAuthenticated: false,
  is2FA: false,
  uuid: undefined,
  isInitialized: false,
  status: 'idle',
  error: undefined,
  user: undefined
};

export const checkAuth = createAsyncThunk<User>('auth/checkAuth', async () => {
  const response = await apiGet('/customer/checkAuth');
  return response.data;
});

export const logOutService = createAsyncThunk('auth/logOut', async () => {
  const response = await apiGet('/users/logOut');
  return response.data;
});

// async thunks here (sideffect actions)
export const signin = createAsyncThunk<
  CustomResponseType,
  AuthPayload,
  { rejectValue: { code: AuthErrorType } }
>('auth/signin', async (payload: AuthPayload, thunkApi) => {
  const response = await apiPost('/users/authenticate', {
    login: payload.email,
    password: payload.password
  });
  if (response.data.code === 'SUCCESS_CODE') {
    //thunkApi.dispatch(checkAuth());
    return response.data;
  }

  return thunkApi.rejectWithValue({
    code: response.data?.code ?? AuthErrorType.ERR_TECHNICAL
  });
});

// 2FA check OTP
export const check2FA = createAsyncThunk<
  CustomResponseType,
  TwoFactorAuthPayload,
  { rejectValue: { code: AuthErrorType } }
>('auth/check2FA', async (payload: TwoFactorAuthPayload, thunkApi) => {
  const response = await apiPost('/users/verify-otp', {
    uuid: payload.uuid,
    otp: payload.otp
  });
  if (response.data.code === 'SUCCESS_CODE') {
    thunkApi.dispatch(checkAuth());
    return response.data;
  }

  return thunkApi.rejectWithValue({
    code: response.data?.code ?? AuthErrorType.ERR_TECHNICAL
  });
});

// send email reset password
export const resetPassword = createAsyncThunk<
  CustomResponseType,
  string,
  { rejectValue: { code: AuthErrorType } }
>('auth/resetPassword', async (email: string, thunkApi) => {
  const response = await apiPost('/users/resetPassword', { mail: email });
  if (response.data?.code === 'SUCCESS_CODE') {
    return response.data;
  }

  return thunkApi.rejectWithValue({
    code:
      response?.data?.code === AuthErrorType.ERR_USER_NOT_FOUND
        ? AuthErrorType.ERR_USER_EMAIL_NOT_FOUND
        : response?.data?.code ?? AuthErrorType.ERR_EMAIL_NOT_SEND
  });
});
// confirme reset password
interface ConfirmPasswordPayload {
  pas: string;
  confirmedPas: string;
  token: string;
}

export const updatePasswordConfirmation = createAsyncThunk<
  CustomResponseType,
  ConfirmPasswordPayload,
  {
    rejectValue: CustomResponseType;
  }
>('auth/updatePasswordConfirmation', async (payload: ConfirmPasswordPayload, thunkApi) => {
  const response = await apiPost('/users/updatePass', payload);
  if (response.data?.code !== 'SUCCESS_CODE') {
    return thunkApi.rejectWithValue({ code: response.data.code, message: response.data.code });
  }

  return response.data;
});

// send email reset password
export interface CustomResponseType {
  code: string;
  message: string;
  error?: any;
  uuid?: any;
}

export const validateResetdToken = createAsyncThunk<
  CustomResponseType,
  string,
  { rejectValue: CustomResponseType }
>('auth/checkToken', async (token: string, thunkApi) => {
  const response = await apiGet(`/users/checkToken/${token}`);
  if (response.data.code !== 'SUCCESS_CODE') {
    return thunkApi.rejectWithValue({
      code: response.data.code,
      message: 'error'
    });
  }
  return response.data;
});

// AUTH SLICE
export const authSlice = createSlice({
  name: 'auth',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    initialized: (state, action: PayloadAction<{ isAuthenticated: boolean }>) => {
      state.isAuthenticated = action.payload.isAuthenticated;
      state.isInitialized = true;
    },
    logout: (state) => {
      state.isAuthenticated = false;
    },
    resetStatus: (state) => {
      state.status = 'idle';
      state.error = undefined;
    },
    close2FA: (state) => {
      state.is2FA = false;
    },
  },

  extraReducers: (builder) => {
    /* signin actions */
    builder.addCase(signin.pending, (state) => {
      state.status = 'loading';
      state.error = undefined;
    });

    builder.addCase(signin.fulfilled, (state, action) => {
      state.status = 'idle';
      state.uuid = action.payload.uuid;
      state.is2FA = true;
    });

    builder.addCase(signin.rejected, (state, action) => {
      state.status = 'error';
      state.error = action.payload.code;
    });

     /* check2FA actions */
     builder.addCase(check2FA.pending, (state) => {
      state.status = 'loading';
      state.error = undefined;
    });

    builder.addCase(check2FA.fulfilled, (state) => {
      state.status = 'idle';
      state.isAuthenticated = true;
      state.is2FA = false;
    });

    builder.addCase(check2FA.rejected, (state, action) => {
      state.status = 'error';
      state.error = action.payload.code;
    });

    /* reset password by email */
    builder.addCase(resetPassword.pending, (state) => {
      state.status = 'loading';
    });

    builder.addCase(resetPassword.fulfilled, (state) => {
      state.status = 'success';
    });

    builder.addCase(resetPassword.rejected, (state, action) => {
      state.status = 'error';
      state.error = action.payload.code;
    });

    /* validate reset password's token */
    builder.addCase(validateResetdToken.pending, (state) => {
      state.status = 'loading';
    });

    builder.addCase(validateResetdToken.fulfilled, (state) => {
      state.status = 'success';
    });

    builder.addCase(validateResetdToken.rejected, (state) => {
      state.status = 'error';
    });

    /* confirm new password */
    builder.addCase(updatePasswordConfirmation.pending, (state) => {
      state.status = 'loading';
    });

    builder.addCase(updatePasswordConfirmation.fulfilled, (state) => {
      state.status = 'success';
    });

    builder.addCase(updatePasswordConfirmation.rejected, (state) => {
      state.status = 'error';
    });

    // set auth true when checkauth returned status 200
    builder.addCase(checkAuth.fulfilled, (state, action) => {
      state.isAuthenticated = true;
      state.isInitialized = true;
      state.user = action.payload;
    });

    // set auth false when checkauth returned status 401
    builder.addCase(checkAuth.rejected, (state) => {
      state.isAuthenticated = false;
      state.isInitialized = true;
    });

    // logout on success
    builder.addCase(logOutService.fulfilled, (state) => {
      state.isAuthenticated = false;
    });

    // logout on error
    builder.addCase(logOutService.rejected, (state) => {
      state.isAuthenticated = false;
    });
  }
});

export const { initialized, logout, resetStatus, close2FA } = authSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectAuth = (state: RootState) => state.auth;
export const selectUser = (state: RootState) => state.auth.user;

export default authSlice.reducer;
